import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Fingerprint, Plus, Trash2, Pencil, Copy, Check, CheckCircle2, AlertCircle, Loader2,
  ChevronDown, ChevronUp, X,
} from 'lucide-react';
import { buildEntraPreset } from '@/lib/ssoPresets';
import { base44 } from '@/api/base44Client';

async function api(method, path, body, organisationId) {
  const r = await base44.tenant.request(method, path, body, organisationId ? { headers: { 'X-Org-Id': organisationId } } : undefined);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

const DEFAULT_ROLES = [['member', 'Member'], ['admin', 'Admin']];

const emptyForm = () => ({
  displayName: '',
  preset: 'entra',
  tenantId: '',
  issuer: '',
  discoveryEndpoint: '',
  issuerEditable: false,
  clientId: '',
  clientSecret: '',
  domain: '',
  defaultRole: 'member',
  emailClaim: '',
  emailClaimTouched: false,
  advancedOpen: false,
});

/** Settings → "Sign-in" tab (IC-008). Organisation SSO provider self-service: list, add (with an
 * Entra preset or generic OIDC), test connection, save, verify domain, edit, delete. Mirrors the
 * IntegrationsCard.jsx pattern (local api() helper, org-scoped via X-Org-Id from localStorage). */
export default function SignInCard({ organisationId }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // providerId being edited, else null (add mode)
  const [reserved, setReserved] = useState(null); // { providerId, callbackUrl } from GET /new (add mode)
  const [reserving, setReserving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, ... } | { ok:false, error }
  const [saving, setSaving] = useState(false);
  const [saveDns, setSaveDns] = useState(null); // { providerId, domain, domainVerificationRecord } after save

  const [dnsShown, setDnsShown] = useState({}); // providerId -> { expected, found, verified }
  const [dnsLoading, setDnsLoading] = useState(null); // providerId currently fetching/verifying
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [copied, setCopied] = useState('');
  const [liveMsg, setLiveMsg] = useState('');

  const firstErrorRef = useRef(null);
  const formHeadingRef = useRef(null);

  const load = () => {
    setListError('');
    return api('GET', '/sso', undefined, organisationId).then(setProviders).catch((e) => setListError(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const entraPreview = useMemo(
    () => (form.preset === 'entra' ? buildEntraPreset(form.tenantId) : null),
    [form.preset, form.tenantId]
  );

  function resetForm() {
    setForm(emptyForm());
    setFormError('');
    setFieldErrors({});
    setTestResult(null);
    setSaveDns(null);
  }

  async function openAddForm() {
    resetForm();
    setEditingId(null);
    setShowForm(true);
    setReserving(true);
    setReserved(null);
    try {
      const r = await api('GET', '/sso/new', undefined, organisationId);
      setReserved(r);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setReserving(false);
    }
  }

  function openEditForm(p) {
    resetForm();
    setEditingId(p.providerId);
    setReserved({ providerId: p.providerId, callbackUrl: p.callbackUrl });
    setForm((f) => ({
      ...f,
      displayName: p.displayName || '',
      preset: 'generic',
      issuer: p.issuer || '',
      discoveryEndpoint: '',
      issuerEditable: true,
      domain: p.domain || '',
      defaultRole: p.defaultRole || 'member',
    }));
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setReserved(null);
    resetForm();
  }

  function setField(name, value) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  // The issuer/discoveryEndpoint actually submitted: derived from the Entra tenant unless the
  // customer has toggled "edit" on the read-only preview, or they're on the generic preset.
  const effectiveIssuer = form.preset === 'entra' && !form.issuerEditable ? (entraPreview?.issuer || '') : form.issuer;
  const effectiveDiscovery = form.preset === 'entra' && !form.issuerEditable ? (entraPreview?.discoveryEndpoint || '') : form.discoveryEndpoint;

  function validateFormFields() {
    const errs = {};
    if (!form.displayName.trim()) errs.displayName = 'Display name is required';
    if (form.preset === 'entra' && !form.issuerEditable) {
      const t = buildEntraPreset(form.tenantId);
      if (!t.valid) errs.tenantId = t.error;
    } else if (!effectiveIssuer.trim()) {
      errs.issuer = 'Issuer is required';
    }
    if (!editingId && !form.clientId.trim()) errs.clientId = 'Client ID is required';
    if (!editingId && !form.clientSecret.trim()) errs.clientSecret = 'Client secret is required';
    if (!form.domain.trim()) errs.domain = 'Email domain is required';
    return errs;
  }

  function focusFirstError(errs) {
    const order = ['displayName', 'tenantId', 'issuer', 'clientId', 'clientSecret', 'domain'];
    const first = order.find((k) => errs[k]);
    if (first && firstErrorRef.current?.name === first) firstErrorRef.current.focus();
    else {
      const el = document.getElementById(`sso-field-${first}`);
      el?.focus();
    }
  }

  async function runTest() {
    const errs = {};
    if (form.preset === 'entra' && !form.issuerEditable) {
      const t = buildEntraPreset(form.tenantId);
      if (!t.valid) errs.tenantId = t.error;
    } else if (!effectiveIssuer.trim()) errs.issuer = 'Issuer is required to test the connection';
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      focusFirstError(errs);
      return;
    }
    setTesting(true);
    setTestResult(null);
    setLiveMsg('Testing connection…');
    try {
      const r = await api('POST', '/sso/test', { issuer: effectiveIssuer, discoveryEndpoint: effectiveDiscovery || undefined }, organisationId);
      setTestResult(r);
      setLiveMsg(r.ok ? 'Connection test succeeded' : `Connection test failed: ${r.error}`);
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
      setLiveMsg(`Connection test failed: ${e.message}`);
    } finally {
      setTesting(false);
    }
  }

  async function save() {
    const errs = validateFormFields();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setFormError('Please fix the highlighted fields.');
      focusFirstError(errs);
      return;
    }
    setFormError('');
    setSaving(true);
    setLiveMsg(editingId ? 'Saving changes…' : 'Registering provider…');
    try {
      const body = {
        displayName: form.displayName.trim(),
        type: 'oidc',
        issuer: effectiveIssuer.trim(),
        discoveryEndpoint: effectiveDiscovery?.trim() || undefined,
        domain: form.domain.trim(),
        defaultRole: form.defaultRole,
      };
      if (form.clientId.trim()) body.clientId = form.clientId.trim();
      if (form.clientSecret.trim()) body.clientSecret = form.clientSecret;
      if (form.emailClaimTouched) body.emailClaim = form.emailClaim.trim() || 'email';

      let result;
      if (editingId) {
        result = await api('PATCH', `/sso/${editingId}`, body, organisationId);
      } else {
        result = await api('POST', '/sso', { ...body, providerId: reserved.providerId, clientId: form.clientId.trim(), clientSecret: form.clientSecret }, organisationId);
      }
      setLiveMsg(editingId ? 'Changes saved' : 'Provider registered');
      if (result.domainVerificationRecord) {
        // Older providers/servers may return the record name without the token. Fetch the
        // active token before showing the setup panel so the user never has to guess the value.
        let dns = result;
        if (!result.domainVerificationToken) {
          const current = await api('POST', `/sso/${result.providerId}/verify-domain`, undefined, organisationId);
          setDnsShown((s) => ({ ...s, [result.providerId]: current }));
          dns = { ...result, domainVerificationToken: current.value };
        }
        setSaveDns({ providerId: dns.providerId, domain: dns.domain, domainVerificationRecord: dns.domainVerificationRecord, domainVerificationToken: dns.domainVerificationToken });
      }
      await load();
      if (!result.domainVerificationRecord) closeForm();
      else { setShowForm(false); } // keep DNS record visible outside the form
    } catch (e) {
      setFormError(e.message);
      setLiveMsg(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function showDnsRecord(providerId) {
    setDnsLoading(providerId);
    try {
      const r = await api('GET', `/sso/${providerId}/dns-record`, undefined, organisationId);
      setDnsShown((s) => ({ ...s, [providerId]: r }));
      setLiveMsg(r.verified ? 'Domain is verified' : 'Domain record shown — not yet verified');
      if (r.verified) await load();
    } catch (e) {
      setListError(e.message);
    } finally {
      setDnsLoading(null);
    }
  }

  async function verifyNow(providerId) {
    setDnsLoading(providerId);
    setLiveMsg('Checking DNS…');
    try {
      const r = await api('POST', `/sso/${providerId}/verify-domain`, undefined, organisationId);
      setDnsShown((s) => ({ ...s, [providerId]: r }));
      setLiveMsg(r.verified ? 'Domain verified' : 'Domain not verified yet — the DNS record was not found');
      if (r.verified) await load();
    } catch (e) {
      setListError(e.message);
      setLiveMsg(`Verification failed: ${e.message}`);
    } finally {
      setDnsLoading(null);
    }
  }

  async function doDelete(providerId) {
    setDnsLoading(providerId);
    try {
      await api('DELETE', `/sso/${providerId}`, undefined, organisationId);
      setConfirmDeleteId(null);
      setLiveMsg('Provider deleted');
      await load();
    } catch (e) {
      setListError(e.message);
    } finally {
      setDnsLoading(null);
    }
  }

  const copy = async (text, tag) => {
    try { await navigator.clipboard.writeText(text); setCopied(tag); setTimeout(() => setCopied(''), 1500); } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="mt-6 bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
      <div aria-live="polite" aria-atomic="true" className="sr-only">{liveMsg}</div>

      <div className="flex items-center gap-2 mb-1">
        <Fingerprint className="w-5 h-5 text-teal-600" aria-hidden="true" />
        <h2 className="text-lg font-bold text-[#0F1C2E]">Single sign-on</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Let your staff sign in with your own identity provider (Microsoft Entra ID or another OpenID
        Connect provider) instead of a password.
      </p>

      <details className="mb-5 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
        <summary className="cursor-pointer text-sm font-semibold text-[#0F1C2E]">
          Setup guide: connect Entra, Google Workspace, or another provider
        </summary>
        <div className="mt-4 space-y-3 text-sm text-slate-700">
          <p>These are organisation SSO connections. They are separate from the platform-wide Google and Microsoft buttons on the main login page.</p>
          <ol className="list-decimal space-y-2 pl-5">
            <li>Choose <strong>Add provider</strong>. Copy the callback URL shown in the form before creating the provider in your identity system.</li>
            <li>Create an OIDC application with that callback URL as a web redirect URI. Keep the client secret available; it is shown only to the identity-system administrator.</li>
            <li>Return here, choose the matching provider type, enter the client details and your organisation email domain, then select <strong>Test connection</strong>.</li>
            <li>Select <strong>Save provider</strong>. Publish the DNS TXT record shown by InclusiveCore, then select <strong>Verify now</strong>. Staff sign-in is blocked until the domain is verified.</li>
          </ol>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <h3 className="font-semibold text-[#0F1C2E]">Microsoft Entra ID</h3>
              <p className="mt-1">Create an App registration, choose the tenant account type your organisation requires, add the displayed callback under Authentication → Web, and use the Directory (tenant) ID, Application (client) ID and client secret here.</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <h3 className="font-semibold text-[#0F1C2E]">Google Workspace</h3>
              <p className="mt-1">Create an OAuth client in Google Cloud, configure the displayed callback as an authorised redirect URI, enable the OpenID scopes, and choose <strong>Other OIDC</strong> here with issuer <code className="rounded bg-slate-100 px-1">https://accounts.google.com</code>.</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <h3 className="font-semibold text-[#0F1C2E]">Other OIDC provider</h3>
              <p className="mt-1">Use the provider’s issuer or discovery URL, client ID and secret. It must support OIDC discovery and return a verified email claim. Common examples include Okta, Auth0 and Keycloak.</p>
            </div>
          </div>
          <p className="text-xs text-slate-500">Use your organisation’s email domain, such as <code className="rounded bg-slate-100 px-1">example.com</code>, rather than a public mailbox domain. If the secret is rotated later, edit the provider and save the new secret; existing secrets are retained when the field is left blank.</p>
        </div>
      </details>

      {listError && <p className="text-sm text-red-600 mb-4" role="alert">{listError}</p>}

      {saveDns && (
        <DnsRecordPanel
          providerId={saveDns.providerId}
          domain={saveDns.domain}
          record={saveDns.domainVerificationRecord}
          value={saveDns.domainVerificationToken}
          onVerify={() => verifyNow(saveDns.providerId)}
          verifying={dnsLoading === saveDns.providerId}
          status={dnsShown[saveDns.providerId]}
          onDismiss={() => { setSaveDns(null); resetForm(); }}
          copy={copy}
          copied={copied}
        />
      )}

      {loading ? (
        <p className="text-sm text-slate-400" role="status">Loading providers…</p>
      ) : providers.length === 0 && !showForm ? (
        <p className="text-sm text-slate-500 mb-4">No identity provider set up yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 mb-5" role="list" aria-label="Organisation SSO providers">
          {providers.map((p) => (
            <li key={p.id} className="py-4">
              {/* Always a stacked (column) layout, never a row: with a chip plus up to three
                  buttons in the controls group, trying to share one row with the name/domain
                  column at any viewport starves that column's width and forces it into an
                  unreadable one-word-per-line wrap (found during e2e screenshot review — see the
                  IC-008 worksheet §6 decision 6). Each block keeps its own natural full width. */}
              <div className="flex flex-col gap-3">
                <div>
                  <p className="font-semibold text-[#0F1C2E]">{p.displayName || p.providerId}</p>
                  <p className="text-sm text-slate-500">{p.domain}</p>
                  <div className="mt-1.5">
                    {p.domainVerified ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                        <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" /> Not verified
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CallbackUrlChip url={p.callbackUrl} onCopy={() => copy(p.callbackUrl, `cb-${p.id}`)} copied={copied === `cb-${p.id}`} />
                  {!p.domainVerified && (
                    <button
                      type="button"
                      onClick={() => showDnsRecord(p.providerId)}
                      disabled={dnsLoading === p.providerId}
                      className="min-h-11 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]"
                    >
                      {dnsLoading === p.providerId ? 'Checking…' : 'Show DNS record'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openEditForm(p)}
                    className="min-h-11 min-w-11 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]"
                    aria-label={`Edit ${p.displayName || p.providerId}`}
                  >
                    <Pencil className="w-4 h-4" aria-hidden="true" /> Edit
                  </button>
                  {confirmDeleteId === p.providerId ? (
                    <div className="flex items-center gap-1.5" role="alertdialog" aria-label={`Confirm delete ${p.displayName || p.providerId}`}>
                      <span className="text-xs text-red-700">Delete this provider?</span>
                      <button
                        type="button"
                        onClick={() => doDelete(p.providerId)}
                        disabled={dnsLoading === p.providerId}
                        className="min-h-11 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                      >
                        {dnsLoading === p.providerId ? 'Deleting…' : 'Confirm delete'}
                      </button>
                      <button type="button" onClick={() => setConfirmDeleteId(null)} className="min-h-11 px-3 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(p.providerId)}
                      className="min-h-11 min-w-11 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm font-medium text-red-700 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                      aria-label={`Delete ${p.displayName || p.providerId}`}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" /> Delete
                    </button>
                  )}
                </div>
              </div>

              {dnsShown[p.providerId] && !p.domainVerified && saveDns?.providerId !== p.providerId && (
                <DnsRecordPanel
                  providerId={p.providerId}
                  domain={p.domain}
                  record={dnsShown[p.providerId].expected}
                  value={dnsShown[p.providerId].value}
                  status={dnsShown[p.providerId]}
                  onVerify={() => verifyNow(p.providerId)}
                  verifying={dnsLoading === p.providerId}
                  copy={copy}
                  copied={copied}
                  compact
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {!showForm && (
        <button
          type="button"
          onClick={openAddForm}
          className="min-h-11 flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] focus-visible:outline-offset-2"
        >
          <Plus className="w-4 h-4" aria-hidden="true" /> Add provider
        </button>
      )}

      {showForm && (
        <div className="mt-2 border border-slate-200 rounded-xl p-5" role="group" aria-labelledby="sso-form-heading">
          <div className="flex items-center justify-between mb-4">
            <h3 id="sso-form-heading" ref={formHeadingRef} tabIndex={-1} className="text-base font-bold text-[#0F1C2E]">
              {editingId ? 'Edit provider' : 'Add provider'}
            </h3>
            <button type="button" onClick={closeForm} aria-label="Close form" className="min-h-11 min-w-11 flex items-center justify-center rounded-lg hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]">
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>

          {reserving ? (
            <p className="text-sm text-slate-400" role="status">Reserving your callback URL…</p>
          ) : reserved ? (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Redirect URI</p>
              <p className="text-xs text-slate-600 mb-1.5">Add this redirect URI to your identity provider first — you'll need it to finish setting up the app registration there.</p>
              <div className="flex items-center gap-2">
                <code className="text-sm break-all">{reserved.callbackUrl}</code>
                <button type="button" onClick={() => copy(reserved.callbackUrl, 'callback')} className="min-h-11 min-w-11 flex items-center justify-center p-1.5 rounded hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]" aria-label="Copy redirect URI">
                  {copied === 'callback' ? <Check className="w-4 h-4 text-teal-600" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                </button>
              </div>
            </div>
          ) : null}

          {formError && <p className="text-sm text-red-600 mb-4" role="alert">{formError}</p>}

          <div className="space-y-5">
            <div>
              <label htmlFor="sso-field-displayName" className="block text-sm font-medium text-slate-700 mb-1.5">
                Display name <span className="text-red-500" aria-label="required">*</span>
              </label>
              <input
                id="sso-field-displayName"
                name="displayName"
                ref={fieldErrors.displayName ? firstErrorRef : undefined}
                type="text"
                value={form.displayName}
                onChange={(e) => setField('displayName', e.target.value)}
                aria-required="true"
                aria-invalid={fieldErrors.displayName ? 'true' : undefined}
                aria-describedby={fieldErrors.displayName ? 'sso-err-displayName' : undefined}
                className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                placeholder="e.g. Acme Ltd (Entra)"
              />
              {fieldErrors.displayName && <p id="sso-err-displayName" className="text-sm text-red-600 mt-1" role="alert">{fieldErrors.displayName}</p>}
            </div>

            <fieldset>
              <legend className="block text-sm font-medium text-slate-700 mb-1.5">Identity provider type</legend>
              <div className="flex gap-2" role="radiogroup" aria-label="Identity provider type">
                {[['entra', 'Microsoft Entra ID'], ['generic', 'Generic OIDC']].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    role="radio"
                    aria-checked={form.preset === val}
                    onClick={() => setField('preset', val)}
                    className={`min-h-11 px-4 py-2 rounded-lg text-sm font-semibold border focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${form.preset === val ? 'bg-[#0F1C2E] text-white border-[#0F1C2E]' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            {form.preset === 'entra' ? (
              <div>
                <label htmlFor="sso-field-tenantId" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Directory (tenant) ID <span className="text-red-500" aria-label="required">*</span>
                </label>
                <input
                  id="sso-field-tenantId"
                  name="tenantId"
                  ref={fieldErrors.tenantId ? firstErrorRef : undefined}
                  type="text"
                  value={form.tenantId}
                  onChange={(e) => setField('tenantId', e.target.value)}
                  aria-required="true"
                  aria-invalid={fieldErrors.tenantId ? 'true' : undefined}
                  aria-describedby={fieldErrors.tenantId ? 'sso-err-tenantId' : 'sso-help-tenantId'}
                  className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  placeholder="3fa85f64-5717-4562-b3fc-2c963f66afa6"
                />
                {fieldErrors.tenantId ? (
                  <p id="sso-err-tenantId" className="text-sm text-red-600 mt-1" role="alert">{fieldErrors.tenantId}</p>
                ) : (
                  <p id="sso-help-tenantId" className="text-xs text-slate-500 mt-1">Found on your Entra app registration's Overview page.</p>
                )}

                {!form.issuerEditable ? (
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Issuer</p>
                      <button type="button" onClick={() => setField('issuerEditable', true)} className="text-xs font-medium text-teal-700 hover:underline min-h-11 flex items-center">edit</button>
                    </div>
                    <p className="text-sm break-all text-slate-700">{entraPreview?.valid ? entraPreview.issuer : '—'}</p>
                    <p className="text-xs text-slate-500 mt-2 break-all">Discovery: {entraPreview?.valid ? entraPreview.discoveryEndpoint : '—'}</p>
                  </div>
                ) : (
                  <div className="mt-3 space-y-3">
                    <IssuerFields form={form} setField={setField} fieldErrors={fieldErrors} firstErrorRef={firstErrorRef} />
                    <button type="button" onClick={() => setField('issuerEditable', false)} className="text-xs font-medium text-teal-700 hover:underline min-h-11 flex items-center">use the Entra preset instead</button>
                  </div>
                )}
              </div>
            ) : (
              <IssuerFields form={form} setField={setField} fieldErrors={fieldErrors} firstErrorRef={firstErrorRef} />
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sso-field-clientId" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Client ID {!editingId && <span className="text-red-500" aria-label="required">*</span>}
                </label>
                <input
                  id="sso-field-clientId"
                  name="clientId"
                  ref={fieldErrors.clientId ? firstErrorRef : undefined}
                  type="text"
                  value={form.clientId}
                  onChange={(e) => setField('clientId', e.target.value)}
                  aria-required={!editingId ? 'true' : undefined}
                  aria-invalid={fieldErrors.clientId ? 'true' : undefined}
                  aria-describedby={fieldErrors.clientId ? 'sso-err-clientId' : (editingId ? 'sso-help-clientId' : undefined)}
                  className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  placeholder={editingId ? 'Leave blank to keep the current client ID' : ''}
                />
                {fieldErrors.clientId ? (
                  <p id="sso-err-clientId" className="text-sm text-red-600 mt-1" role="alert">{fieldErrors.clientId}</p>
                ) : editingId ? (
                  <p id="sso-help-clientId" className="text-xs text-slate-500 mt-1">Leave blank to keep the current client ID.</p>
                ) : null}
              </div>
              <div>
                <label htmlFor="sso-field-clientSecret" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Client secret {!editingId && <span className="text-red-500" aria-label="required">*</span>}
                </label>
                <input
                  id="sso-field-clientSecret"
                  name="clientSecret"
                  ref={fieldErrors.clientSecret ? firstErrorRef : undefined}
                  type="password"
                  autoComplete="off"
                  value={form.clientSecret}
                  onChange={(e) => setField('clientSecret', e.target.value)}
                  aria-required={!editingId ? 'true' : undefined}
                  aria-invalid={fieldErrors.clientSecret ? 'true' : undefined}
                  aria-describedby={fieldErrors.clientSecret ? 'sso-err-clientSecret' : (editingId ? 'sso-help-clientSecret' : undefined)}
                  className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  placeholder={editingId ? 'Leave blank to keep' : ''}
                />
                {fieldErrors.clientSecret ? (
                  <p id="sso-err-clientSecret" className="text-sm text-red-600 mt-1" role="alert">{fieldErrors.clientSecret}</p>
                ) : editingId ? (
                  <p id="sso-help-clientSecret" className="text-xs text-slate-500 mt-1">Leave blank to keep the current secret.</p>
                ) : null}
              </div>
            </div>

            {editingId && (form.clientId.trim() || (form.preset === 'generic' && form.issuer.trim() && form.issuer.trim() !== providerIssuerOf(editingId, providers))) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Changing the issuer or client ID signs out everyone who signed in through this provider — they'll need to sign in again.
              </p>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sso-field-domain" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email domain <span className="text-red-500" aria-label="required">*</span>
                </label>
                <input
                  id="sso-field-domain"
                  name="domain"
                  ref={fieldErrors.domain ? firstErrorRef : undefined}
                  type="text"
                  value={form.domain}
                  onChange={(e) => setField('domain', e.target.value)}
                  aria-required="true"
                  aria-invalid={fieldErrors.domain ? 'true' : undefined}
                  aria-describedby={fieldErrors.domain ? 'sso-err-domain' : undefined}
                  className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                  placeholder="acme.com"
                />
                {fieldErrors.domain && <p id="sso-err-domain" className="text-sm text-red-600 mt-1" role="alert">{fieldErrors.domain}</p>}
              </div>
              <div>
                <label htmlFor="sso-field-defaultRole" className="block text-sm font-medium text-slate-700 mb-1.5">Default role for new sign-ins</label>
                <select
                  id="sso-field-defaultRole"
                  value={form.defaultRole}
                  onChange={(e) => setField('defaultRole', e.target.value)}
                  className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                >
                  {DEFAULT_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setField('advancedOpen', !form.advancedOpen)}
                aria-expanded={form.advancedOpen}
                aria-controls="sso-advanced-panel"
                className="min-h-11 flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-[#0F1C2E]"
              >
                {form.advancedOpen ? <ChevronUp className="w-4 h-4" aria-hidden="true" /> : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
                Advanced
              </button>
              {form.advancedOpen && (
                <div id="sso-advanced-panel" className="mt-3">
                  <label htmlFor="sso-field-emailClaim" className="block text-sm font-medium text-slate-700 mb-1.5">Email claim</label>
                  <input
                    id="sso-field-emailClaim"
                    type="text"
                    value={form.emailClaimTouched ? form.emailClaim : (form.emailClaim || 'email')}
                    onChange={(e) => setForm((f) => ({ ...f, emailClaim: e.target.value, emailClaimTouched: true }))}
                    aria-describedby="sso-help-emailClaim"
                    className="w-full min-h-11 max-w-xs border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                    placeholder="email"
                  />
                  <p id="sso-help-emailClaim" className="text-xs text-slate-500 mt-1">
                    The token claim that carries the user's email address. Use <code>preferred_username</code> if your Entra tenant omits <code>email</code>.
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="button"
                onClick={runTest}
                disabled={testing}
                className="min-h-11 flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="min-h-11 flex items-center gap-2 px-5 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] focus-visible:outline-offset-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Save provider'}
              </button>
            </div>

            {testResult && (
              <div aria-live="polite" className={`rounded-lg border p-3 text-sm ${testResult.ok ? 'bg-teal-50 border-teal-200 text-teal-800' : 'bg-red-50 border-red-200 text-red-700'}`} role={testResult.ok ? 'status' : 'alert'}>
                {testResult.ok ? (
                  <div>
                    <p className="font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Connection succeeded</p>
                    <dl className="mt-2 text-xs space-y-1">
                      <div><dt className="inline font-semibold">Authorization:</dt> <dd className="inline break-all">{testResult.authorizationEndpoint}</dd></div>
                      <div><dt className="inline font-semibold">Token:</dt> <dd className="inline break-all">{testResult.tokenEndpoint}</dd></div>
                      <div><dt className="inline font-semibold">JWKS:</dt> <dd className="inline break-all">{testResult.jwksEndpoint}</dd></div>
                    </dl>
                  </div>
                ) : (
                  <p className="flex items-center gap-1.5"><AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" /> {testResult.error}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function providerIssuerOf(providerId, providers) {
  return providers.find((p) => p.providerId === providerId)?.issuer || '';
}

function IssuerFields({ form, setField, fieldErrors, firstErrorRef }) {
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="sso-field-issuer" className="block text-sm font-medium text-slate-700 mb-1.5">
          Issuer <span className="text-red-500" aria-label="required">*</span>
        </label>
        <input
          id="sso-field-issuer"
          name="issuer"
          ref={fieldErrors.issuer ? firstErrorRef : undefined}
          type="text"
          value={form.issuer}
          onChange={(e) => setField('issuer', e.target.value)}
          aria-required="true"
          aria-invalid={fieldErrors.issuer ? 'true' : undefined}
          aria-describedby={fieldErrors.issuer ? 'sso-err-issuer' : undefined}
          className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
          placeholder="https://idp.example.com"
        />
        {fieldErrors.issuer && <p id="sso-err-issuer" className="text-sm text-red-600 mt-1" role="alert">{fieldErrors.issuer}</p>}
      </div>
      <div>
        <label htmlFor="sso-field-discoveryEndpoint" className="block text-sm font-medium text-slate-700 mb-1.5">Discovery URL (optional)</label>
        <input
          id="sso-field-discoveryEndpoint"
          type="text"
          value={form.discoveryEndpoint}
          onChange={(e) => setField('discoveryEndpoint', e.target.value)}
          className="w-full min-h-11 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
          placeholder="Defaults to {issuer}/.well-known/openid-configuration"
        />
      </div>
    </div>
  );
}

function CallbackUrlChip({ url, onCopy, copied }) {
  return (
    <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 min-h-11">
      <span className="text-xs text-slate-500 hidden sm:inline">Redirect URI:</span>
      <code className="text-xs truncate max-w-[10rem] sm:max-w-[16rem]">{url}</code>
      <button type="button" onClick={onCopy} aria-label="Copy redirect URI" className="min-h-11 min-w-11 -my-2.5 flex items-center justify-center p-1 rounded hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]">
        {copied ? <Check className="w-3.5 h-3.5 text-teal-600" aria-hidden="true" /> : <Copy className="w-3.5 h-3.5" aria-hidden="true" />}
      </button>
    </div>
  );
}

function DnsRecordPanel({ providerId, domain, record, value, status, onVerify, verifying, onDismiss, copy, copied, compact }) {
  return (
    <div className={`rounded-xl bg-amber-50 border border-amber-200 p-4 ${compact ? 'mt-3' : 'mb-5'}`} role="status">
      <p className="text-sm font-semibold text-amber-800 mb-1">Add this DNS TXT record to verify {domain}</p>
      <div className="grid gap-2 mb-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
        <strong className="text-xs uppercase tracking-wide text-amber-800">Name</strong>
        <code className="text-sm break-all">{record}</code>
        {record && <button type="button" onClick={() => copy(record, `dns-name-${providerId}`)} aria-label="Copy DNS record name" className="min-h-11 min-w-11 flex items-center justify-center p-1.5 rounded hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-600">{copied === `dns-name-${providerId}` ? <Check className="w-4 h-4 text-teal-600" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}</button>}
        <strong className="text-xs uppercase tracking-wide text-amber-800">Value</strong>
        <code className="text-sm break-all">{value || 'Verification value unavailable — click Verify now to retrieve it.'}</code>
        {value && <button type="button" onClick={() => copy(value, `dns-value-${providerId}`)} aria-label="Copy DNS record value" className="min-h-11 min-w-11 flex items-center justify-center p-1.5 rounded hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-600">{copied === `dns-value-${providerId}` ? <Check className="w-4 h-4 text-teal-600" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}</button>}
      </div>
      <p className="text-xs text-amber-700 mb-3">Create a <strong>TXT</strong> record with exactly these two fields. Some DNS providers append <code>.{domain}</code> automatically when you enter the name; that is expected. Do not put quotation marks around the value unless your DNS provider adds them for you.</p>
      {status && (
        status.verified ? (
          <p className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Verified</p>
        ) : (
          <p className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            Not verified yet — the record was not an exact match{status.found?.length ? <>. DNS returned <code>{status.found.join(', ')}</code> at that name.</> : ' and no TXT value was returned at that name.'} The expected value is shown above.
          </p>
        )
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onVerify}
          disabled={verifying}
          className="min-h-11 flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-700"
        >
          {verifying ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
          {verifying ? 'Checking…' : 'Verify now'}
        </button>
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="min-h-11 px-3 py-2 rounded-lg text-sm text-amber-800 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-700">
            Done for now
          </button>
        )}
      </div>
    </div>
  );
}
